/**
 * LastDecisionBanner — surfaces *who* did *what*, *when*, and *why* on an
 * immersion entry, so admin / pre-recruteur can see the decision context
 * directly in the calendar drawer (no need to click "Voir la fiche").
 *
 * Tones:
 *   • rejected     → red    · ThumbsDown
 *   • validated    → emerald · ThumbsUp
 *   • immersion_done → blue · Trophy
 *   • no_show      → amber  · UserX
 *
 * The `decision` shape comes from the backend
 * `_LastDecision` model in routes/immersions.py.
 */
import { ThumbsDown, ThumbsUp, Trophy, UserX } from "lucide-react";

import { formatDateTime } from "../../lib/format";


const TONE = {
    rejected: {
        Icon: ThumbsDown,
        wrap: "bg-red-50 border-red-200",
        accent: "text-red-700",
        chip: "bg-red-100 text-red-800",
        title: "Refusé",
    },
    validated: {
        Icon: ThumbsUp,
        wrap: "bg-emerald-50 border-emerald-200",
        accent: "text-emerald-700",
        chip: "bg-emerald-100 text-emerald-800",
        title: "Validé",
    },
    immersion_done: {
        Icon: Trophy,
        wrap: "bg-sky-50 border-sky-200",
        accent: "text-sky-700",
        chip: "bg-sky-100 text-sky-800",
        title: "Immersion faite",
    },
    no_show: {
        Icon: UserX,
        wrap: "bg-amber-50 border-amber-200",
        accent: "text-amber-700",
        chip: "bg-amber-100 text-amber-900",
        title: "No-show",
    },
};


export default function LastDecisionBanner({ decision, dense = false, className = "" }) {
    if (!decision || !decision.kind) return null;
    const t = TONE[decision.kind];
    if (!t) return null;
    const { Icon } = t;

    return (
        <div
            data-testid={`last-decision-${decision.kind}`}
            className={`rounded-2xl border-2 ${t.wrap} ${dense ? "p-2.5" : "p-3"} ${className}`}
        >
            <div className="flex items-start gap-2.5">
                <div className={`h-7 w-7 rounded-xl ${t.chip} inline-flex items-center justify-center shrink-0`}>
                    <Icon className="h-3.5 w-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className={`text-xs font-extrabold ${t.accent} flex items-center gap-2 flex-wrap`}>
                        <span>{t.title}</span>
                        {decision.by_name && (
                            <span className="text-[10px] font-bold text-slate-500">
                                par <span className={t.accent}>{decision.by_name}</span>
                            </span>
                        )}
                        {decision.at && (
                            <span className="text-[10px] font-semibold text-slate-500">
                                · {formatDateTime(decision.at)}
                            </span>
                        )}
                    </div>
                    {(decision.reason_label || decision.comment) && (
                        <div className="mt-1 text-[11px] text-slate-700 leading-snug break-words">
                            {decision.reason_label && (
                                <span className="font-bold">Motif : {decision.reason_label}</span>
                            )}
                            {decision.reason_label && decision.comment && <span> · </span>}
                            {decision.comment && (
                                <span className="italic">«&nbsp;{decision.comment}&nbsp;»</span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
